import { embedAuth } from './auth'
import { InstrumentData, Option } from './fields'

const BASE = '/api/v1/temp-embed'

export class EmbedRequestError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'EmbedRequestError'
  }
}

async function request<T>(path: string, method = 'GET', data?: InstrumentData): Promise<T> {
  if (!embedAuth.token) throw new EmbedRequestError('缺少访问凭证，请使用完整的访问地址', 401)
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 20000)
  try {
    const response = await fetch(BASE + path, {
      method,
      // Unlike same-origin axios defaults, this also omits existing system cookies.
      credentials: 'omit',
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${embedAuth.token}`,
        ...(data ? { 'Content-Type': 'application/json' } : {})
      },
      body: data ? JSON.stringify(data) : undefined
    })
    const result = await response.json().catch(() => null)
    if (!response.ok || !result || result.success !== true) {
      throw new EmbedRequestError(result?.message || '请求失败，请稍后重试', response.status)
    }
    return result.data as T
  } catch (error) {
    if (error instanceof EmbedRequestError) throw error
    throw new EmbedRequestError('请求超时或网络不可用，请稍后重试', 0)
  } finally {
    window.clearTimeout(timeout)
  }
}

export interface InstrumentPage {
  items: InstrumentData[]
  total: number
  'device_classifications': string[]
}

export interface InstrumentFilters {
  'department_id'?: number | ''
  'custodian_id'?: number | ''
  'device_classification'?: string
}

export interface InstrumentOptions {
  'device_classifications': string[]
  'asset_natures': string[]
}

export const embedApi = {
  list: (page: number, perPage: number, keyword: string, filters: InstrumentFilters) => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage), keyword })
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    })
    return request<InstrumentPage>(`/instruments?${params}`)
  },
  detail: (id: number) => request<InstrumentData>(`/instruments/${id}`),
  create: (data: InstrumentData) => request<InstrumentData>('/instruments', 'POST', data),
  update: (id: number, data: InstrumentData) => request<InstrumentData>(`/instruments/${id}`, 'PUT', data),
  remove: (id: number) => request<void>(`/instruments/${id}`, 'DELETE'),
  persons: () => request<Option[]>('/persons'),
  departments: () => request<Option[]>('/departments'),
  options: () => request<InstrumentOptions>('/instrument-options')
}
